<template>
	<div class="url-stats-section" :class="{ 'full': full, 'minimal': minimal }">
		<div class="url-stats-header" :class="{ 'no-margin': !showUrlStats }" @click="toggleUrlStats" v-if="stats.title">
			<h3 class="url-stats-title">{{ stats.title }}</h3>
			<span class="url-stats-toggle" :class="{ expanded: showUrlStats }">
				<font-awesome-icon :icon="showUrlStats ? 'eye-slash' : 'eye'" />
			</span>
		</div>
		<div v-if="showUrlStats">
			<!-- Single grid (backward compatibility) -->
			<div 
				v-if="stats.stats && !isMultiGrid" 
				class="url-stats-grid"
			>
				<div class="url-stat-item" v-for="stat in stats.stats" :key="stat.type" :class="{ 'clickable': stat.click }" @click="clicked(stat)">
					<div class="stat-value" :title="stat.title||''">
						{{ stat.value }}
						<span 
							v-if="stat.change" 
							:class="['stat-change', stat.change.outcome]" 
							 :title="stat.change.oldValue||''"
						>
							{{ stat.change.direction }}{{ stat.change.value }}{{ stat.change.type === 'percentage' ? '%' : '' }}
						</span>
					</div>
					<div class="stat-label">{{ stat.label }}</div>
				</div>
			</div>

			<!-- Multiple grids -->
			<template v-if="isMultiGrid">
				<div 
					v-for="(gridStats, gridKey) in gridData" 
					:key="gridKey"
					:class="['url-stats-grid', { 'small': gridStats.config && gridStats.config.small }]"
					:style="gridStats.config && gridStats.config.marginBottom ? { marginBottom: gridStats.config.marginBottom } : {}"
				>
					<div class="url-stat-item" v-for="stat in gridStats.stats" :key="stat.type" :class="{ 'clickable': stat.click }" @click="clicked(stat)" :title="(stat.change&&stat.change.oldValue)?stat.change.oldValue:''">
						<div class="stat-value" :title="stat.title||''">
							{{ stat.value }}
							<span 
								v-if="stat.change" 
								:class="['stat-change', stat.change.outcome]" 
								 :title="stat.change.oldValue||''"
							>
								{{ stat.change.direction }}{{ stat.change.value }}{{ stat.change.type === 'percentage' ? '%' : '' }}
							</span>
						</div>
						<div class="stat-label">{{ stat.label }}</div>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
export default {
	name: "StatsOverview",
	props: {
		stats: {
			type: Object,
			required: true
		},
		full: {
			type: Boolean,
			default: true
		},
		minimal: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			showUrlStats: true
		};
	},
	computed: {
		isMultiGrid() {
			// Check if we have multiple grids by looking for arrays or grid configs
			return Object.keys(this.stats).some(key => {
				return key !== 'title' && 
					   key !== 'stats' && 
					   (Array.isArray(this.stats[key]) || 
					    (typeof this.stats[key] === 'object' && this.stats[key] !== null && 
					     (this.stats[key].stats || this.stats[key].small)));
			});
		},
		gridData() {
			if (!this.isMultiGrid) return {};
			
			const grids = {};
			
			Object.keys(this.stats).forEach(key => {
				if (key !== 'title' && key !== 'stats') {
					const value = this.stats[key];
					
					if (Array.isArray(value)) {
						// Direct array of stats
						grids[key] = {
							stats: value,
							config: {}
						};
					} else if (typeof value === 'object' && value !== null) {
						// Object with stats and/or config
						grids[key] = {
							stats: value.stats || [],
							config: {
								small: value.small || false,
								marginBottom: value.marginBottom || null
							}
						};
					}
				}
			});
			
			return grids;
		},

	},
	methods: {
		toggleUrlStats(event) {
			event.stopPropagation(); // Prevent triggering the design style cycle
			this.showUrlStats = !this.showUrlStats;
		},
		clicked(stat) {
			if ( stat.click ) {
				this.$emit( stat.click, stat );
			}
		},

	}
};
</script>

<style lang="less" scoped>
	@import "./../../assets/css/less/_settings.less";
	@import "./../../assets/css/less/_helper.less";

	.url-stats-section {
		background-color: #f8f9fa;
		border: 1px solid #e9ecef;
		border-radius: 8px;
		margin: 20px 0 5px;
		padding: 20px;

		&.full {

			margin-bottom: 20px;

		}

		&.minimal {
			margin: 0;
			padding: 0;
			background-color: transparent;
			border: none;
		}

		.url-stats-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			cursor: pointer;
			margin-bottom: 15px;
			.trans;

			&.no-margin {
				margin-bottom: 0;
			}

			&:hover {
				.url-stats-title {
					color: @formDefaultColour;
				}
				.url-stats-toggle {
					color: @formDefaultColour;
				}
			}
		}

		.url-stats-title {
			color: @darkContrast;
			font-size: 18px;
			font-weight: @bold;
			margin: 0;
			.trans;
		}

		.url-stats-toggle {
			color: @darkContrast;
			font-size: 16px;
			.trans;

			&.expanded {
				transform: rotate(0deg);
			}
		}

		.url-stats-grid {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
			gap: 16px;
			margin-bottom: 15px;
			perspective: 1000px;

			&:last-child {
				margin-bottom: 0;
			}

			&.small {
				grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
				gap: 10px;

				.url-stat-item {
					padding: 10px 8px;

					.stat-value {
						font-size: 18px;
						margin-bottom: 4px;

						.stat-change {
							font-size: 10px;

							svg {
								width: 8px;
								height: 8px;
							}
						}
					}

					.stat-label {
						font-size: 10px;
					}
				}
			}
		}

		.url-stat-item {
			background: #ffffff;
			border: 1px solid rgba(0, 0, 0, 0.06);
			border-radius: 8px;
			box-shadow: 
				0 1px 3px rgba(0, 0, 0, 0.08),
				0 4px 12px rgba(0, 0, 0, 0.04);
			padding: 18px 14px;
			text-align: center;
			position: relative;
			transform-style: preserve-3d;
			.trans;

			&.clickable {
				cursor: pointer;

				&:hover {
					transform: translateY(-1px);
					box-shadow: 
						0 2px 8px rgba(0, 0, 0, 0.12),
						0 6px 16px rgba(0, 0, 0, 0.06);
					border-color: rgba(0, 0, 0, 0.1);
				}
			}

			.stat-value {
				color: @formDefaultColour;
				font-size: 24px;
				font-weight: 700;
				line-height: 1.2;
				margin-bottom: 5px;
				position: relative;
				transform-style: preserve-3d;
				.trans;

				.stat-change {
					display: inline-block;
					font-size: 12px;
					font-weight: 500;
					vertical-align: middle;
					.trans;

					svg {
						width: 10px;
						height: 10px;
						margin-right: 2px;
					}

					&.good {
						color: @green;
					}

					&.bad {
						color: @red;
					}
				}
			}

			.stat-label {
				color: @darkContrast;
				font-size: 12px;
				font-weight: 500;
				text-transform: uppercase;
				letter-spacing: 0.5px;
				transform-style: preserve-3d;
				.trans;
			}
		}
	}

	@media screen and (max-width: 767px) {
		.url-stats-section {
			margin: 15px 0 5px;
			padding: 15px;

			.url-stats-header {
				margin-bottom: 10px;

				&.no-margin {
					margin-bottom: 0;
				}
			}

			.url-stats-title {
				font-size: 16px;
			}

			.url-stats-toggle {
				font-size: 14px;
			}

			.url-stats-grid {
				grid-template-columns: repeat(2, 1fr);
				gap: 10px;

				&.small {
					grid-template-columns: repeat(3, 1fr);
					gap: 8px;

					.url-stat-item {
						padding: 8px 6px;

						.stat-value {
							font-size: 14px;

							.stat-change {
								font-size: 9px;

								svg {
									width: 7px;
									height: 7px;
								}
							}
						}

						.stat-label {
							font-size: 9px;
						}
					}
				}
			}

			.url-stat-item {
				padding: 12px 8px;

				.stat-value {
					font-size: 20px;

					.stat-change {
						font-size: 11px;

						svg {
							width: 9px;
							height: 9px;
						}
					}
				}

				.stat-label {
					font-size: 11px;
				}
			}
		}
	}

	@media screen and (min-width: 768px) and (max-width: 1023px) {
		.url-stats-section {
			.url-stats-grid {
				grid-template-columns: repeat(3, 1fr);

				&.small {
					grid-template-columns: repeat(4, 1fr);
				}
			}
		}
	}


</style>
